import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

/**
 * Normalize dialog `conversionType` to `xmltojson` | `hl7tojson`.
 * @example normalizeRoutingConversionType('HL7') → 'hl7tojson'; normalizeRoutingConversionType('') → 'xmltojson'
 */
export function normalizeRoutingConversionType(raw: unknown): 'xmltojson' | 'hl7tojson' {
  const v = `${raw ?? ''}`.trim().toLowerCase();
  if (!v) {
    return 'xmltojson';
  }
  if (v === 'hl7tojson' || (v.includes('hl7') && !v.includes('xml'))) {
    return 'hl7tojson';
  }
  return 'xmltojson';
}

/**
 * Returns true when the conversion type resolves to HL7 routing rules.
 * @example isHl7RoutingConversion('hl7tojson') → true; isHl7RoutingConversion('xmltojson') → false
 */
export function isHl7RoutingConversion(raw: unknown): boolean {
  return normalizeRoutingConversionType(raw) === 'hl7tojson';
}

/**
 * True when a stored rule field looks like an HL7 path or flat HL7 key.
 * @example isHl7PersistedFieldValue('PID.5.1') → true; isHl7PersistedFieldValue('Case/AccessionNumber') → false
 */
export function isHl7PersistedFieldValue(raw: unknown): boolean {
  const v = `${raw ?? ''}`.trim();
  if (!v) {
    return false;
  }
  if (/^hl7message\//i.test(v) || v.includes('[@parentId')) {
    return true;
  }
  return /^[A-Za-z0-9]{2,4}\.\d+(?:\.\d+)*$/.test(v);
}

/**
 * ArrowLeft/ArrowRight moves focus between adjacent inputs in a segmented field (e.g. SEG . 1 . 1 . 1).
 * Pass `shell` as an element or CSS selector scoped from the focused input.
 */
export function handleSegmentedArrowNav(
  event: KeyboardEvent,
  shell: HTMLElement | string
): boolean {
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
    return false;
  }
  const input = event.target as HTMLInputElement;
  if (!input?.tagName || input.tagName !== 'INPUT') {
    return false;
  }
  const shellEl =
    typeof shell === 'string' ? (input.closest(shell) as HTMLElement | null) : shell;
  if (!shellEl) {
    return false;
  }
  const inputs = Array.from(shellEl.querySelectorAll('input')) as HTMLInputElement[];
  const currentIndex = inputs.indexOf(input);
  if (currentIndex < 0) {
    return false;
  }
  const selStart = input.selectionStart ?? 0;
  const selEnd = input.selectionEnd ?? 0;
  const len = input.value.length;
  const atStart = selStart === 0 && selEnd === 0;
  const atEnd = selStart === len && selEnd === len;

  if (event.key === 'ArrowLeft' && atStart && currentIndex > 0) {
    event.preventDefault();
    const prev = inputs[currentIndex - 1];
    prev.focus();
    const pos = prev.value.length;
    prev.setSelectionRange(pos, pos);
    return true;
  }
  if (event.key === 'ArrowRight' && atEnd && currentIndex < inputs.length - 1) {
    event.preventDefault();
    const next = inputs[currentIndex + 1];
    next.focus();
    next.setSelectionRange(0, 0);
    return true;
  }
  return false;
}

/** Opens mat-autocomplete on ArrowDown when the panel is closed. */
export function handleMatAutocompleteArrowKeys(
  event: KeyboardEvent,
  trigger: MatAutocompleteTrigger | null | undefined
): boolean {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
    return false;
  }
  if (!trigger || trigger.panelOpen) {
    return false;
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    trigger.openPanel();
    return true;
  }
  return false;
}

/** ArrowUp/Down for plain inputs backed by a fixed option list (HTML datalist). */
export function handlePlainListArrowKeys(
  event: KeyboardEvent,
  options: readonly string[],
  getValue: () => string,
  setValue: (value: string) => void
): boolean {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
    return false;
  }
  const direction: 1 | -1 = event.key === 'ArrowDown' ? 1 : -1;
  const next = cycleStringInList(options, getValue(), direction);
  if (next === getValue()) {
    return false;
  }
  event.preventDefault();
  setValue(next);
  return true;
}

function cycleStringInList(
  options: readonly string[],
  current: string,
  direction: 1 | -1
): string {
  if (!options.length) {
    return current;
  }
  const list = options.map((o) => (o ?? '').toString()).filter(Boolean);
  if (!list.length) {
    return current;
  }
  const normalized = (current ?? '').trim().toLowerCase();
  let idx = list.findIndex((o) => o.toLowerCase() === normalized);
  if (idx < 0) {
    const prefixMatches = normalized
      ? list.filter((o) => o.toLowerCase().startsWith(normalized))
      : list;
    const pool = prefixMatches.length ? prefixMatches : list;
    return direction > 0 ? pool[0] : pool[pool.length - 1];
  }
  const next = (idx + direction + list.length) % list.length;
  return list[next];
}

/**
 * Scrolls an open mat-autocomplete / overlay panel with the mouse wheel
 * (capture-phase listener; used when the panel is inside a scrollable dialog).
 */
export class RoutingAutocompletePanelHelper {
  private wheelHandler: ((e: WheelEvent) => void) | null = null;
  private panelSelector = '';

  attach(panelSelector: string): void {
    this.detach();
    this.panelSelector = panelSelector;
    this.wheelHandler = (e: WheelEvent) => {
      const panel = document.querySelector(this.panelSelector) as HTMLElement | null;
      if (!panel || panel.scrollHeight <= panel.clientHeight) {
        return;
      }
      panel.scrollTop += e.deltaY;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('wheel', this.wheelHandler, { capture: true, passive: false });
  }

  detach(): void {
    if (this.wheelHandler) {
      document.removeEventListener('wheel', this.wheelHandler, { capture: true });
      this.wheelHandler = null;
    }
  }

  destroy(): void {
    this.detach();
  }
}
